/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2021, 2024
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/

import {
  InlineNotification,
  Link,
  NumberInput,
  Checkbox,
  SkeletonText,
  Button
} from '@carbon/react';
import { Edit, Launch } from '@carbon/react/icons';
import _ from 'lodash';
import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';

import { generateDiscoveryLink } from '@ibm-watson/shared/modules/discovery/util/search-skill';
import usePrevious from '@ibm-watson/shared/modules/ia/hooks/usePrevious';
import { isFeatureEnabled } from '@ibm-watson/shared/modules/ia/store/features/selectors';
import { selectIsNewIa } from '@ibm-watson/shared/modules/ia/store/instance/selectors';
import { DISCOVERY_TEMPLATE_ID } from '@ibm-watson/shared/modules/ia/store/skills/helper';
import { isCpd } from '@ibm-watson/shared/modules/ia/util/config';
import { ctaEvents } from '@ibm-watson/shared/modules/ia/util/trackers/segment';
import { FeatureFlag } from '../../../../ts/feature-flags';
import { ElasticSearchConfigFields } from '../../../elastic-search/components/elastic-search-config-fields/ElasticSearchConfigFields';
import { ConversationalSearchSection } from '../../../ia/components/conversational-search/ConversationalSearchSection';
import { generateDocsUrl } from '../../../ia/util/docs';
import {
  listInstances,
  listProjectsIfRequired,
  listFields,
  listFieldExamples
} from '../../store/actions';
import {
  clearInstanceSettings,
  setIsChangingProject,
  setIsForceInstanceList
} from '../../store/reducer';
import {
  selectIsForceInstanceList,
  selectAreInstancesLoading,
  selectProject,
  selectInstanceById,
  selectFields,
  selectFieldExamples
} from '../../store/selectors';
import {
  NUMBER_OF_RESULTS,
  ASSISTANT_NEW_VERSION,
  CONFIDENCE_THRESHOLD_NUMBER
} from '../../util/constants';
import { ConfigFields } from './config-fields/ConfigFields';
import { ConfigOption } from './config-option/ConfigOption';
import { messages } from './messages';
import { SearchPreview } from './search-preview/SearchPreview';
import { SearchResultCustomFilter } from './search-result-dialogs/SearchResultCustomFilter';
import { SearchResultDialogs } from './search-result-dialogs/SearchResultDialogs';
import { SkillConnectionInformationHeader } from './skill-connection-information-header/SkillConnectionInformationHeader';

import './SearchDetailConfig.scss';

// TODO ES: Needs to be updated to a tsx file.

// Draft will be different depending on if it's for elastic or disco.

// TODO ES: Instead of passing draft as a prop this could probably be accomplished by doing 'const draft = elasticSetup
// ? useElasticDraft() : useDiscoveryDraft();'
export const SearchDetailConfig = ({
  draft,
  readOnly,
  elasticSetup = false
}) => {
  const { formatMessage: t } = useIntl();
  const dispatch = useDispatch();

  const isConversationalSearchEnabled = useSelector(state =>
    isFeatureEnabled(state, FeatureFlag.DISCOVERY_LLM_CONVERSATIONAL_SEARCH)
  );

  const discoveryInstanceId = draft.discoveryInstanceId;
  const selectedDiscoveryInstance = useSelector(
    state => selectInstanceById(state, discoveryInstanceId),
    shallowEqual
  );
  const areDiscoveryInstancesLoading = useSelector(selectAreInstancesLoading);
  const prevDiscoveryInstLoading = usePrevious(areDiscoveryInstancesLoading);
  const [unreachedDiscoveryInstance, setUnreachedDiscoveryInstance] =
    useState(false);
  // if we display the notice, also set flag to force the instance re-list if re-opened
  const isForceDiscoveryInstanceList = useSelector(selectIsForceInstanceList);
  const newIA = useSelector(selectIsNewIa);

  const discoveryProjectId = draft.discoveryProjectId;
  const discoverySelectedProject = useSelector(
    state => selectProject(state, discoveryInstanceId, discoveryProjectId),
    shallowEqual
  );
  const disabled =
    readOnly || areDiscoveryInstancesLoading || unreachedDiscoveryInstance;

  const discoveryFields = useSelector(selectFields, shallowEqual);
  const discoveryFieldExamples = useSelector(selectFieldExamples, shallowEqual);

  // For Discovery, handle setting if the chosen Discovery instance is unreachable for the user.
  useEffect(() => {
    if (!elasticSetup) {
      if (areDiscoveryInstancesLoading) {
        setUnreachedDiscoveryInstance(false);
      } else {
        // UserA and UserB in the same Assistant may have different Discovery services available
        //  based on Discovery membership/roles -- if UserB opens a search that UserA has configured
        //  and the the Discovery service selectedDiscoveryInstance is missing, we display a Not Available note
        //  (the unreachedDisco displays the message) and record that we should force re-listing instances
        //  when a Search is next re-opened (by isForceDiscoveryInstanceList)
        if (
          prevDiscoveryInstLoading &&
          discoveryInstanceId &&
          !selectedDiscoveryInstance
        ) {
          // mark the flags - note these are reset separately
          setUnreachedDiscoveryInstance(true);
          dispatch(setIsForceInstanceList(true));
        }
      }
    }
  }, [
    selectedDiscoveryInstance,
    discoveryInstanceId,
    areDiscoveryInstancesLoading,
    prevDiscoveryInstLoading,
    dispatch,
    elasticSetup
  ]);

  // For Discovery, fetch the Discovery instances or projects if appropriate.
  useEffect(() => {
    if (!elasticSetup) {
      if (
        !selectedDiscoveryInstance &&
        isForceDiscoveryInstanceList &&
        !unreachedDiscoveryInstance
      ) {
        dispatch(setIsForceInstanceList(false));
        dispatch(listInstances());
      }
      if (selectedDiscoveryInstance && !selectedDiscoveryInstance.projects) {
        dispatch(listProjectsIfRequired(selectedDiscoveryInstance.id));
      }
    }
  }, [
    elasticSetup,
    dispatch,
    isForceDiscoveryInstanceList,
    selectedDiscoveryInstance,
    unreachedDiscoveryInstance
  ]);

  // For Discovery, fetch the fields and field examples if a Discovery instance and a project have been chosen.
  useEffect(() => {
    if (!elasticSetup) {
      if (discoveryProjectId && selectedDiscoveryInstance?.id) {
        dispatch(
          listFields({
            instanceId: selectedDiscoveryInstance.id,
            projectId: discoveryProjectId
          })
        );
        dispatch(
          listFieldExamples({
            instanceId: selectedDiscoveryInstance.id,
            projectId: discoveryProjectId
          })
        );
      }
    }
  }, [
    elasticSetup,
    discoveryProjectId,
    dispatch,
    selectedDiscoveryInstance?.id
  ]);

  const loadingText = <SkeletonText lineCount={1} />;

  const searchPreviewPayload = input => {
    // this is payload for preview call!

    return {
      //id: ,
      input: {
        message_type: 'text',
        text: input
      },
      skill_config: {
        instance_id: discoveryInstanceId,
        ...(discoveryProjectId && {
          project_id: discoveryProjectId
        }),
        //version: config.version,
        pagination_enabled: false, // optional, defaults to false
        message: draft.successMessage,
        error_message: draft.connectivityMessage,
        no_result_message: draft.noResultsMessage,
        highlight: true,
        schema_mapping_title: draft.titleOptionValue,
        schema_mapping_body: draft.bodyOptionValue,
        schema_mapping_url: draft.urlOptionValue,
        ...(!!draft.maxPrimaryResults && {
          assistant_version: ASSISTANT_NEW_VERSION,
          max_primary_results: draft.maxPrimaryResults,
          max_total_results: draft.isDisplayShowMore
            ? NUMBER_OF_RESULTS.SHOW_MORE_MAX
            : draft.maxPrimaryResults
        }),
        ...(draft.isRefinedResults && {
          confidence_threshold: CONFIDENCE_THRESHOLD_NUMBER
        }),
        ...(draft.isHighlightAnswers && {
          find_answers: draft.isHighlightAnswers
        })
      },
      config: {
        instance_id: discoveryInstanceId
      },
      worker_template_id: DISCOVERY_TEMPLATE_ID,
      agent_metadata: {
        assistant_id: '00000000-0000-0000-000000000000',
        skill_reference: 'Search skill preview',
        session_id: '00000000-0000-0000-000000000000'
      }
    };
  };

  const setNumberOfResults = ({ imaginaryTarget }) => {
    if (imaginaryTarget) {
      const value = imaginaryTarget.value;

      const calmaxPrimaryResults =
        value === '' || value === 0 || value > NUMBER_OF_RESULTS.MAX
          ? ''
          : Number(value);
      draft.setMaxPrimaryResults(calmaxPrimaryResults);

      if (draft.maxPrimaryResults) {
        ctaEvents.searchShowTopNResults({
          resultValue: draft.maxPrimaryResults
        });
      }
    }
  };

  const generateDiscoverySkill = () => {
    const result = {};
    if (discoveryProjectId !== undefined) {
      result.config = { ...result.config, project_id: discoveryProjectId };
    }
    return result;
  };

  const discoveryLink = selectedDiscoveryInstance?.region
    ? generateDiscoveryLink(
        selectedDiscoveryInstance,
        window.location,
        'configuration',
        generateDiscoverySkill(),
        false
      )
    : undefined;

  const titleBodyUrlFound =
    !!draft.titleOptionValue ||
    !!draft.bodyOptionValue ||
    !!draft.urlOptionValue;

  const discoveryInfo = {
    list: [
      {
        title: t(messages.discoInstance),
        value: selectedDiscoveryInstance
          ? selectedDiscoveryInstance?.name ||
            selectedDiscoveryInstance?.entity.name
          : unreachedDiscoveryInstance
          ? t(messages.notAvailable)
          : ''
      },

      {
        title: t(messages.discoProject),
        value: unreachedDiscoveryInstance
          ? t(messages.notAvailable)
          : discoverySelectedProject?.name || loadingText
      }
    ],
    actions: [
      {
        type: 'secondary',
        text: (
          <div className="open-in-discovery-wrapper">
            {t(messages.openInDiscovery)}
            <Launch
              size={16}
              className="bx--btn__icon"
              aria-label={t(messages.openInDiscovery)}
            />
          </div>
        ),
        href: disabled ? null : discoveryLink,
        onClick: disabled ? null : () => ctaEvents.searchOpenInDiscovery(),
        disabled:
          disabled ||
          (isCpd && !_.get(selectedDiscoveryInstance, 'tooling_url'))
      }
    ]
  };

  const onToggleRefinedResults = () => {
    draft.setIsRefinedResults(!draft.isRefinedResults);
    ctaEvents.searchRefinedResults({
      resultValue: draft.isRefinedResults ? 'off' : 'on'
    });
  };

  const onToggleHighlightAnswers = () => {
    draft.setIsHighlightAnswers(!draft.isHighlightAnswers);
    ctaEvents.searchHighlightAnswers({
      resultValue: draft.isHighlightAnswers ? 'off' : 'on'
    });
  };

  const displayShowMoreLink = value => {
    draft.setIsDisplayShowMore(value);
    ctaEvents.searchDisplayShowMoreResults({
      resultValue: draft.isDisplayShowMore ? 'unselected' : 'selected'
    });
  };

  const handleEditDiscovery = event => {
    event.preventDefault();
    // set isChangingProject and then send listInstance call (not IfRequired flavor) to reset SearchInstance page
    dispatch(setIsChangingProject(true));
    dispatch(listInstances());
    dispatch(clearInstanceSettings());
  };

  const getAdjustedFieldExample = field => {
    const fieldsArray = field.split('.');
    let adjustedValue = '';
    // Fields comeback as a string e.g metadata.source.url, Field examples can be of any nested form
    // e.g metadata[0].source[0].url or metadata.source.url or metadata[0].source.url
    // Following maps field string with nested id and adjust field string accordingly
    fieldsArray.forEach((field, i) => {
      adjustedValue += field;
      if (i < fieldsArray.length - 1) {
        if (Array.isArray(_.get(discoveryFieldExamples[0], adjustedValue))) {
          adjustedValue = `${adjustedValue}[0].`;
        } else {
          adjustedValue = `${adjustedValue}.`;
        }
      }
    });
    let example = _.get(discoveryFieldExamples[0], adjustedValue);
    //Sometimes we do get back example as an array which is a noisy response from disco v2
    // so need to consider that case as well.
    if (Array.isArray(example) && example.length > 0) {
      example = example[0]?.trim();
    }
    return example
      ? t(messages.example) + example
      : t(messages.fieldDefaultExample);
  };

  const docLink = elasticSetup
    ? generateDocsUrl(
        '/watson-assistant?topic=watson-assistant-search-elasticsearch-add'
      ) + '#setup-elasticsearch'
    : newIA
    ? generateDocsUrl('/watson-assistant?topic=watson-assistant-search-add') +
      '#search-add-configure'
    : generateDocsUrl('/?topic=assistant-skill-search-add') +
      '#skill-search-add-troubleshoot';

  // Items to display in fields dropdown
  const items = () => {
    const getExample = field =>
      discoveryFieldExamples.length === 0
        ? t(messages.loading)
        : getAdjustedFieldExample(field);

    const getElement = field => (
      <div className="field-container">
        <span>{field}</span>
        {discoveryFieldExamples.length === 0 ? (
          loadingText
        ) : (
          <span className="field-example">{getExample(field)}</span>
        )}
      </div>
    );

    return discoveryFields.map(({ field }) => ({
      id: field,
      text: field,
      label: field,
      element: getElement(field),
      example: getExample(field)
    }));
  };

  return (
    <>
      <div className="SearchDetailConfig">
        <div className="SearchDetailConfig-container">
          <div className="configuration-introduction-wrapper">
            <div className="configuration-introduction">
              <p>
                {t(messages.intro)}
                <Link
                  target="_blank"
                  rel="noopener noreferrer"
                  className="link--learn-more"
                  href={docLink}>
                  {t(messages.learnMore)}
                </Link>
              </p>
            </div>
            {!elasticSetup && (
              <div>
                <SkillConnectionInformationHeader
                  className="SearchSkillConfig__skill-infomation"
                  information={discoveryInfo}
                />
                <Button
                  onClick={handleEditDiscovery}
                  className="SearchDetailConfig__edit-connect-button"
                  renderIcon={Edit}
                  iconDescription={t(messages.editDiscovery)}
                  kind="tertiary"
                  size="sm">
                  {t(messages.editDiscovery)}
                </Button>
              </div>
            )}
          </div>
          <div className="SearchDetailConfig__configure-content-wrapper">
            <div className="SearchDetailConfig__configure-content-section">
              <h4 className="SearchDetailConfig__header">
                {t(messages.configTitle)}
              </h4>
              {elasticSetup ? (
                <ElasticSearchConfigFields />
              ) : (
                <ConfigFields
                  items={items()}
                  titleOptionValue={draft.titleOptionValue}
                  bodyOptionValue={draft.bodyOptionValue}
                  urlOptionValue={draft.urlOptionValue}
                  setTitleOptionValue={draft.setTitleOptionValue}
                  setBodyOptionValue={draft.setBodyOptionValue}
                  setUrlOptionValue={draft.setUrlOptionValue}
                  loadingFields={!discoveryFields.length}
                  readOnly={disabled}
                />
              )}
            </div>
          </div>
          <ConversationalSearchSection draft={draft} readOnly={readOnly} />
          <SearchResultDialogs
            successMessage={draft.successMessage}
            setSuccessMessage={draft.setSuccessMessage}
            noResultsMessage={draft.noResultsMessage}
            setNoResultsMessage={draft.setNoResultsMessage}
            connectivityMessage={draft.connectivityMessage}
            setConnectivityMessage={draft.setConnectivityMessage}
            hideSuccessMessageTab={
              isConversationalSearchEnabled &&
              draft.isConversationalSearchEnabled
            }
            readOnly={disabled}
          />
          {isConversationalSearchEnabled &&
            draft.isConversationalSearchEnabled && (
              <>
                <InlineNotification
                  className="SearchDetailConfig__conversational-search-inline"
                  kind="warning"
                  lowContrast={true}
                  title={t(messages.conversationalSearchInlineWarningTitle)}
                  subtitle={t(
                    messages.conversationalSearchInlineWarningSubtitle
                  )}
                  hideCloseButton={true}
                />
                <SearchResultDialogs
                  successMessage={draft.successMessage}
                  setSuccessMessage={draft.setSuccessMessage}
                  onlyShowDisabledSuccessMessageTab={
                    isConversationalSearchEnabled &&
                    draft.isConversationalSearchEnabled
                  }
                />
              </>
            )}
          {!isCpd && !elasticSetup && (
            <ConfigOption
              header={t(messages.emphasizeAnswers)}
              body={t(messages.emphasizeAnswersTitle)}
              tooltip={t(messages.emphasizeAnswersToolTip)}
              toggleAriaLabel={t(messages.emphasizeAnswersAriaLabel)}
              toggled={draft.isHighlightAnswers}
              onToggle={onToggleHighlightAnswers}
              disabled={
                (isConversationalSearchEnabled &&
                  draft.isConversationalSearchEnabled) ||
                disabled
              }
            />
          )}
          {!elasticSetup && (
            <ConfigOption
              header={t(messages.resultsQtyTitle)}
              disabled={
                (isConversationalSearchEnabled &&
                  draft.isConversationalSearchEnabled) ||
                disabled
              }>
              <>
                <div className="SearchDetailConfig__adjustResultsQty-container">
                  {t(messages.resultsQtyShowTopResults)}
                  <NumberInput
                    id="SearchDetailConfig__adjustResultsQty-numberInput"
                    invalidText={t(messages.invalidNum)}
                    max={NUMBER_OF_RESULTS.MAX}
                    min={NUMBER_OF_RESULTS.MIN}
                    value={draft.maxPrimaryResults}
                    onChange={setNumberOfResults}
                    onClick={setNumberOfResults}
                    disabled={
                      (isConversationalSearchEnabled &&
                        draft.isConversationalSearchEnabled) ||
                      disabled
                    }
                  />
                  {t(messages.results)}
                </div>
                <Checkbox
                  id="SearchDetailConfig__showMaxResults"
                  className="SearchDetailConfig__showMaxResults"
                  checked={draft.isDisplayShowMore}
                  labelText={t(messages.resultsQtyShowMoreLink)}
                  onChange={displayShowMoreLink}
                  disabled={
                    (isConversationalSearchEnabled &&
                      draft.isConversationalSearchEnabled) ||
                    disabled
                  }
                />
              </>
            </ConfigOption>
          )}
          {!elasticSetup && (
            <ConfigOption
              header={t(messages.refineResultsHeader)}
              body={t(messages.refineResultsTitle)}
              tooltip={t(messages.refineResultsToolTip)}
              toggleAriaLabel={t(messages.refineResultsAriaLabel)}
              toggled={draft.isRefinedResults}
              onToggle={onToggleRefinedResults}
              disabled={
                (isConversationalSearchEnabled &&
                  draft.isConversationalSearchEnabled) ||
                disabled
              }
            />
          )}
          {!elasticSetup && <SearchResultCustomFilter readOnly={disabled} />}
          {!!discoveryFields.length &&
            !!selectedDiscoveryInstance &&
            !elasticSetup && (
              <SearchPreview
                payload={input => searchPreviewPayload(input)}
                hasAnyFieldsSelected={titleBodyUrlFound}
                initiateWebchat={!!discoveryFields.length}
                isConversationalSearch={
                  isConversationalSearchEnabled &&
                  draft.isConversationalSearchEnabled
                }
              />
            )}
        </div>
      </div>
    </>
  );
};

SearchDetailConfig.propTypes = {
  draft: PropTypes.object.isRequired,
  elasticSetup: PropTypes.bool,
  readOnly: PropTypes.bool
};
